import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { Button, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import Label from '../Label';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import toastr from '../../../toastr';

interface IProps {
  applyForcedPricing: (netPriceForce: number, listPriceForce: number, tradePriceForce: number) => void;
  clearForcedPricing: () => void;
  container: Object;
  data: {
    escalatorListPrice: string;
    escalatorTradePrice: string;
    listPrice: string;
    netPrice: string;
    partNum: string;
    tradePrice: string;
  };
  forceExists: boolean;
  hide: Function;
  show: boolean;
  showGrossProfitPercentage: boolean;
  showNetPrice: boolean;
}

/* refs within the ForcePartModal stopped working properly when we upgraded to React 16.
   I submitted an issue to the rbs team here: https://github.com/react-bootstrap/react-bootstrap/issues/2841

   The resolution will apparently be awaiting the next point release of react-bootstrap, followed by replacing
   my inline ref methods with bound functions.  For now, I am using setTimeouts to obtain non-null refs within
   componentDidUpdate().

   TODO: Change obtaining refs to use bound functions rather than inline functions.
         See Dan Abramov's explanation: https://github.com/facebook/react/issues/11258#issuecomment-337379350
   TODO: Update to the next release of react-bootstrap (following 0.31.3) when the rbs team tags an npm release.
         Commit c9ab07d resolves issues with modal refs.
*/

@observer
class ForcePartModal extends Component<IProps, {}> {
  @observable applyButtonDisabled = true;
  @observable grossProfitPercentage = '';

  applyButtonRef: HTMLButtonElement;
  listPriceForceInput: HTMLInputElement;
  markupOverrideInput: HTMLInputElement;
  netPriceForceInput: HTMLInputElement;
  sellPriceForceInput: HTMLInputElement;

  componentDidMount() {
    // Timeout required until RBS fixes their React 16 async slot issues
    setTimeout(() => {
      this.netPriceForceInput.focus();
      if (this.props.forceExists) {
        this.netPriceForceInput.setSelectionRange(0, this.netPriceForceInput.value.length);
      }
      this.setForcePartModalState();
    }, 0);
  }

  apply() {
    // This should only be clickable if sell/trade are properly set, check anyway.
    const netPriceForce = parseFloat(this.deriveNetPrice());
    const listPriceForce = parseFloat(this.listPriceForceInput.value);
    const tradePriceForce = parseFloat(this.sellPriceForceInput.value); // trade == sell

    if (isNaN(netPriceForce) || isNaN(listPriceForce) || isNaN(tradePriceForce)) {
      toastr.error('Error', 'Invalid price detected.  Try again.');
      return;
    }

    this.props.applyForcedPricing(netPriceForce, listPriceForce, tradePriceForce);
  }

  // If a net price is not entered, default to the seed (likely published net unless previously forced)
  deriveNetPrice() {
    return this.netPriceForceInput.value.trim() === '' ? this.props.data.netPrice : this.netPriceForceInput.value;
  }

  overrideList(advanceField: boolean) {
    const listCurrent = this.props.data.listPrice;
    const listOverride = this.listPriceForceInput.value.trim();
    const netCurrent = this.props.data.netPrice;

    // User or markup system has already entered a value?  Make sure it's a valid X.XX format and move on to sell field.
    // In the case markup is entered, manually changing this field will dominate the markup calculation.
    if (listOverride !== '') {
      const parsedListOverride = parseFloat(listOverride);
      if (!isNaN(parsedListOverride)) {
        this.listPriceForceInput.value = parsedListOverride.toFixed(2);
        this.setForcePartModalState();
        if (advanceField) {
          this.sellPriceForceInput.focus();
          this.sellPriceForceInput.setSelectionRange(0, this.sellPriceForceInput.value.length);
        }
        return;
      }
      // else user entered junk input ("asdf") and we apply the logic below
    }

    // Here we logically have a blank input (or invalid input)

    // If net override is empty (possible in the case we aren't showing default net prices to user)
    // or the current net price is the same as default net price, simply use D3/QM list price (no escalator)
    const netOverride = this.netPriceForceInput.value.trim(); // assumed safe from net blur logic
    if (netOverride === '' || netOverride === netCurrent) {
      // This is the result in the case no markup has been specified
      this.listPriceForceInput.value = listCurrent;
      this.setForcePartModalState();

      // Otherwise apply system pricing escalator for list
    } else {
      const escalatorList = parseFloat(this.props.data.escalatorListPrice);

      if (isNaN(escalatorList)) {
        this.listPriceForceInput.value = listCurrent;
      } else {
        this.listPriceForceInput.value = (escalatorList * parseFloat(netOverride)).toFixed(2);
      }

      // Go ahead and escalate sell price as well if it's empty
      // This will automatially call setForcePartModalState()
      this.overrideSell(false);
    }

    // If a markup HAS been specified (and net is set), discard the previous if/else field
    // calculation and apply the net+markup instead (again, only on blank/invalid list input).
    this.overrideMarkup(false);

    if (advanceField) {
      this.sellPriceForceInput.focus();
      this.sellPriceForceInput.setSelectionRange(0, this.sellPriceForceInput.value.length);
    }
  }

  overrideMarkup(advanceField: boolean) {
    const markup = parseInt(this.markupOverrideInput.value.trim(), 10);
    if (!isNaN(markup)) {
      if (markup === 0) {
        toastr.error('Error', '0% Not Accepted for Markup');
        this.markupOverrideInput.value = '';
        return;
      }

      this.markupOverrideInput.value = markup.toString(); // cleanup input if necessary (20% => 20)

      // apply markups to list/sell values, potentially overwriting whatever is there (markup use dominates)
      const parsedNetOverride = parseFloat(this.deriveNetPrice());
      if (!isNaN(parsedNetOverride)) {
        this.listPriceForceInput.value = ((1 + (2 * markup) / 100) * parsedNetOverride).toFixed(2);
        this.sellPriceForceInput.value = ((1 + markup / 100) * parsedNetOverride).toFixed(2);
      }

      // otherwise junk entered for markup
    } else {
      this.markupOverrideInput.value = '';
    }

    this.setForcePartModalState();

    if (advanceField) {
      this.listPriceForceInput.focus();
      this.listPriceForceInput.setSelectionRange(0, this.listPriceForceInput.value.length);
    }
  }

  overrideNet(advanceField: boolean) {
    const netCurrent = this.props.data.netPrice;
    const netOverride = this.netPriceForceInput.value.trim();

    // Empty net override input?  Use existing net if we're revealing net prices to counter person
    if (netOverride === '') {
      if (this.props.showNetPrice) {
        this.netPriceForceInput.value = netCurrent;
      }

      // Otherwise, parse user input into correct 2 decimal format.
    } else {
      const parsedNetOverride = parseFloat(netOverride);

      // if unparaseable use net price from d3/qm eval if we're revealing net prices to counter person
      if (isNaN(parsedNetOverride)) {
        if (this.props.showNetPrice) {
          this.netPriceForceInput.value = netCurrent;
        } else {
          this.netPriceForceInput.value = '';
        }
      } else {
        this.netPriceForceInput.value = parsedNetOverride.toFixed(2);
      }
    }

    // Apply any existing markup, if already present (i.e. editing net after setting net/markup once)
    this.overrideMarkup(false);

    this.setForcePartModalState();

    if (advanceField) {
      this.markupOverrideInput.focus();
      this.markupOverrideInput.setSelectionRange(0, this.markupOverrideInput.value.length);
    }
  }

  overrideSell(advanceField: boolean) {
    const netCurrent = this.props.data.netPrice;
    const sellCurrent = this.props.data.tradePrice;
    const sellOverride = this.sellPriceForceInput.value.trim();

    // User or markup system has already entered a value?  Make sure it's a valid X.XX format and move on to apply button.
    // In the case markup is entered, manually changing this field will dominate the markup calculation.
    if (sellOverride !== '') {
      const parsedSellOverride = parseFloat(sellOverride);
      if (!isNaN(parsedSellOverride)) {
        this.sellPriceForceInput.value = parsedSellOverride.toFixed(2);
        this.setForcePartModalState();
        if (advanceField) {
          // Give Apply button time to enable
          setTimeout(() => {
            (ReactDOM.findDOMNode(this.applyButtonRef) as HTMLButtonElement).focus();
          }, 0);
        }
        return;
      }
      // else user entered junk input ("asdf") and we apply the logic below
    }

    // Here we logically have a blank input (or invalid input)

    // If net override is empty (possible in the case we aren't showing default net prices to user)
    // or the current net price is the same as default net price, simply use D3/QM sell price (no escalator)
    const netOverride = this.netPriceForceInput.value.trim(); // assumed safe from net blur logic
    if (netOverride === '' || netOverride === netCurrent) {
      this.sellPriceForceInput.value = sellCurrent;

      // Otherwise apply system pricing escalator for list
    } else {
      const escalatorTrade = parseFloat(this.props.data.escalatorTradePrice);

      if (isNaN(escalatorTrade)) {
        this.sellPriceForceInput.value = sellCurrent;
      } else {
        this.sellPriceForceInput.value = (escalatorTrade * parseFloat(netOverride)).toFixed(2);
      }
    }

    this.setForcePartModalState();

    // If a markup HAS been specified (and net is set), discard the previous if/else field
    // calculation and apply the net+markup instead (again, only on blank/invalid sell input).
    this.overrideMarkup(false);

    if (advanceField) {
      setTimeout(() => {
        (ReactDOM.findDOMNode(this.applyButtonRef) as HTMLButtonElement).focus();
      }, 0);
    }
  }

  // Determines if the "Apply" button should be active (all req fields present)
  setForcePartModalState() {
    if (this.listPriceForceInput.value !== '' && this.sellPriceForceInput.value !== '') {
      this.applyButtonDisabled = false;

      // Calculate gross profit %
      // This should only run if net/sell/trade are properly set, check anyway.
      const netPriceForce = parseFloat(this.deriveNetPrice());
      const listPriceForce = parseFloat(this.listPriceForceInput.value);
      const tradePriceForce = parseFloat(this.sellPriceForceInput.value); // trade == sell

      if (!(isNaN(netPriceForce) || isNaN(listPriceForce) || isNaN(tradePriceForce)) && tradePriceForce !== 0) {
        this.grossProfitPercentage = Math.trunc(((tradePriceForce - netPriceForce) / tradePriceForce) * 100).toFixed(0);
      } else {
        this.grossProfitPercentage = '';
      }
    } else {
      this.applyButtonDisabled = true;
      this.grossProfitPercentage = '';
    }
  }

  render() {
    // Popover to explain multi-use list input (dollar or % markup, analogous to PIN1)
    const listPricePopover = (
      <Popover id="modal-popover">
        <Popover.Title>% MARKUP HELP</Popover.Title>
        <Popover.Content>
          % of markup will apply to <strong>SELL</strong> price.
          <br />
          2*% of markup will apply to <strong>LIST</strong> price.
          <br />
          <em>Leave blank to use pricing escalators.</em>
        </Popover.Content>
      </Popover>
    );

    return (
      <Modal
        backdrop="static"
        container={this.props.container as any}
        dialogClassName="sk-forcePartModal"
        onHide={() => this.props.hide()}
        show={this.props.show}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>{this.props.data.partNum}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="sk-pricingControls">
            <div>
              <Label>NET</Label>
              <input
                defaultValue={this.props.showNetPrice ? this.props.data.netPrice : ''}
                onBlur={() => this.overrideNet(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideNet(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.netPriceForceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">
                {!this.props.forceExists && this.props.showNetPrice && this.props.data.netPrice}
              </div>
            </div>
            <div>
              <Label>MARKUP %</Label>
              <input
                onBlur={() => this.overrideMarkup(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideMarkup(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.markupOverrideInput = ref)}
                type="text"
              />
              <br />
              <OverlayTrigger overlay={listPricePopover}>
                <div className="sk-listPriceOverrideHelp">
                  <i className="zmdi zmdi-help" />
                </div>
              </OverlayTrigger>
            </div>
            <div>
              <Label>LIST</Label>
              <input
                defaultValue={this.props.forceExists ? this.props.data.listPrice : ''}
                onBlur={() => this.overrideList(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideList(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.listPriceForceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">{!this.props.forceExists && this.props.data.listPrice}</div>
            </div>
            <div>
              <Label>SELL</Label>
              <input
                defaultValue={this.props.forceExists ? this.props.data.tradePrice : ''}
                onBlur={() => this.overrideSell(false)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    this.overrideSell(true);
                  }
                }}
                ref={(ref: HTMLInputElement) => (this.sellPriceForceInput = ref)}
                type="text"
              />
              <div className="sk-currentPrice">{!this.props.forceExists && this.props.data.tradePrice}</div>
            </div>
          </div>
          {this.props.showGrossProfitPercentage && this.grossProfitPercentage !== '' && (
            <div className="sk-grossProfitPercentage">Gross Profit: {this.grossProfitPercentage}%</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="sk-forcePartModalFooter">
            <div>
              {this.props.forceExists && (
                <Button
                  variant="warning"
                  onClick={() => {
                    this.applyButtonDisabled = true;
                    this.props.clearForcedPricing();
                  }}
                >
                  Clear Force
                </Button>
              )}
            </div>

            <div>
              <Button
                onClick={() => {
                  if (!this.props.forceExists) {
                    this.applyButtonDisabled = true;
                  }
                  this.props.hide();
                }}
                variant="secondary"
              >
                Cancel
              </Button>

              <Button
                variant="success"
                disabled={this.applyButtonDisabled}
                onClick={() => this.apply()}
                ref={(ref: HTMLButtonElement) => (this.applyButtonRef = ref)}
              >
                Apply
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ForcePartModal;
